import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useLang } from "../i18n/LangProvider";
import { POTENCE_HERO } from "../i18n/translations";

const Hero = ({ onReserveClick, onMenuClick }) => {
  const { t } = useLang();

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={POTENCE_HERO}
          alt="La Potence — viande flambée suspendue à la fonte forgée"
          className="w-full h-full object-cover object-center"
        />
        {/* Vignette */}
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-obsidian/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian/70 via-transparent to-transparent" />
      </div>

      {/* Top floating tag */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.8 }}
        className="absolute top-28 right-6 md:right-12 hidden md:flex items-center gap-3 text-ivory/70"
      >
        <span className="w-2 h-2 rounded-full bg-copper flicker" />
        <span className="overline">est. 1987</span>
      </motion.div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-end pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="divider-line" />
            <span className="overline">{t.hero.location}</span>
          </div>

          <h1
            data-testid="hero-title"
            className="font-serif font-light text-ivory text-6xl sm:text-7xl lg:text-[8.5rem] leading-[0.9] tracking-tighter"
          >
            La{" "}
            <span className="italic text-copper font-serif-italic">
              Potence
            </span>
          </h1>

          <p className="mt-6 max-w-xl font-serif italic text-2xl sm:text-3xl text-ivory/85 leading-snug">
            {t.hero.subtitle}
          </p>

          <p className="mt-6 max-w-xl text-ivory/65 text-base sm:text-lg leading-relaxed">
            {t.hero.description}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-reserve-button"
              onClick={onReserveClick}
              className="btn-primary"
            >
              {t.hero.ctaReserve} <ArrowRight size={16} />
            </button>
            <button
              data-testid="hero-menu-button"
              onClick={onMenuClick}
              className="btn-secondary"
            >
              {t.hero.ctaMenu}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 right-6 md:right-12 hidden md:flex flex-col items-center gap-2 text-ivory/50"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] rotate-90 origin-center mb-8">
          {t.hero.scroll}
        </span>
        <ChevronDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
};

export default Hero;
